import { useState, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { GraduationCap, ArrowLeft, Check, X, ChevronRight, Award, RotateCcw } from 'lucide-react';
import {
  fetchQuizTopicsThunk, fetchQuizThunk, submitQuizThunk,
} from '../../redux/credentialQuiz/credentialQuizActionThunks';
import {
  selectQuizTopics, selectActiveQuiz, selectLastQuizResult, selectQuizLoading, selectQuizError, clearActiveQuiz,
} from '../../redux/credentialQuiz/credentialQuizSlice';

const LEVEL_MAX = { beginner: 1, intermediate: 2, advanced: 2 };
const levelLabel = (level, t) => t(`credentialQuiz.levels.${level}`);

// Standalone Knowledge Assessment -- topic browse (grouped, per the original
// spec's own suggested grouping), one-question-at-a-time quiz-taking, and a
// results view. Deliberately has zero connection anywhere to credibility
// score/tier -- see credential_quiz_model.py's docstring for why. This is a
// real, testable knowledge-verification feature on its own merits.
const CredentialQuizPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const topics = useSelector(selectQuizTopics);
  const activeQuiz = useSelector(selectActiveQuiz);
  const lastResult = useSelector(selectLastQuizResult);
  const loading = useSelector(selectQuizLoading);
  // Was fully tracked in the slice (submitQuizThunk.rejected sets this)
  // but never imported/rendered here -- a failed quiz submission was
  // 100% silent.
  const quizError = useSelector(selectQuizError);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({}); // { questionId: choiceKey }

  useEffect(() => {
    if (topics.length === 0) dispatch(fetchQuizTopicsThunk());
  }, [dispatch, topics.length]);

  const grouped = useMemo(() => {
    const groups = {};
    topics.forEach((t) => {
      const g = t.group_label || 'Other';
      if (!groups[g]) groups[g] = [];
      groups[g].push(t);
    });
    return groups;
  }, [topics]);

  const startQuiz = (topicId, level) => {
    setCurrentIndex(0);
    setAnswers({});
    dispatch(fetchQuizThunk({ topicId, level }));
  };

  const selectAnswer = (questionId, choiceKey) => {
    setAnswers((a) => ({ ...a, [questionId]: choiceKey }));
  };

  const submit = () => {
    const payload = Object.entries(answers).map(([question_id, selected_choice_key]) => ({
      question_id: Number(question_id), selected_choice_key,
    }));
    dispatch(submitQuizThunk({ topicId: activeQuiz.topicId, level: activeQuiz.level, answers: payload }));
  };

  const exitQuiz = () => {
    dispatch(clearActiveQuiz());
    dispatch(fetchQuizTopicsThunk());
  };

  // ---- Results view ----
  if (lastResult) {
    const pct = lastResult.questions_total > 0 ? Math.round((lastResult.questions_correct / lastResult.questions_total) * 100) : 0;
    return (
      <div className="min-h-full max-w-2xl mx-auto p-6" style={{ background: '#09090f' }}>
        <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
          <Award className="w-10 h-10 text-amber-400 mx-auto mb-3" />
          <h2 className="text-2xl font-bold text-white mb-1">{t('credentialQuiz.correctPct', { pct })}</h2>
          <p className="text-gray-400 text-sm mb-4">
            {t('credentialQuiz.scoreSummary', { correct: lastResult.questions_correct, total: lastResult.questions_total, level: levelLabel(lastResult.level, t) })}{' '}
            <span className="text-white font-medium">{lastResult.level_score.toFixed(2)} / {LEVEL_MAX[lastResult.level]}</span>
          </p>
          <div className="space-y-3 text-left mb-6">
            {lastResult.results.map((r) => (
              <div key={r.question_id} className={`p-3 rounded-xl border text-sm ${r.was_correct ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-red-500/5 border-red-500/20'}`}>
                <div className="flex items-center gap-2">
                  {r.was_correct ? <Check size={14} className="text-emerald-400" /> : <X size={14} className="text-red-400" />}
                  <span className="text-gray-300">
                    {t('credentialQuiz.yourAnswer')} <strong className="text-white">{r.selected_choice_key}</strong>
                    {!r.was_correct && <> {t('credentialQuiz.correctAnswer')} <strong className="text-white">{r.correct_choice_key}</strong></>}
                  </span>
                </div>
                {r.explanation && <p className="text-xs text-gray-500 mt-1.5 ml-6">{r.explanation}</p>}
              </div>
            ))}
          </div>
          <button onClick={exitQuiz} className="px-5 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-sm font-medium">
            {t('credentialQuiz.backToTopics')}
          </button>
        </div>
      </div>
    );
  }

  // ---- Quiz-taking view ----
  if (activeQuiz) {
    const q = activeQuiz.questions[currentIndex];
    const isLast = currentIndex === activeQuiz.questions.length - 1;
    const allAnswered = activeQuiz.questions.every((qq) => answers[qq.id]);

    return (
      <div className="min-h-full max-w-2xl mx-auto p-6" style={{ background: '#09090f' }}>
        <button onClick={exitQuiz} className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-6">
          <ArrowLeft size={16} /> {t('credentialQuiz.exitQuiz')}
        </button>
        <div className="flex items-center justify-between mb-4">
          <span className="text-xs text-gray-500 uppercase tracking-wide">{levelLabel(activeQuiz.level, t)}</span>
          <span className="text-xs text-gray-500">{t('credentialQuiz.questionCounter', { current: currentIndex + 1, total: activeQuiz.questions.length })}</span>
        </div>
        {q && (
          <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
            <p className="text-white text-lg font-medium mb-5">{q.question_text}</p>
            <div className="space-y-2">
              {q.choices.map((c) => (
                <button
                  key={c.key}
                  onClick={() => selectAnswer(q.id, c.key)}
                  className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${
                    answers[q.id] === c.key
                      ? 'bg-violet-500/20 border-violet-500/50 text-white'
                      : 'bg-gray-900/40 border-gray-700/50 text-gray-300 hover:border-gray-600'
                  }`}
                >
                  <span className="font-medium text-gray-500 mr-2">{c.key.toUpperCase()}.</span> {c.text}
                </button>
              ))}
            </div>
            {quizError && (
              <p className="mt-4 text-sm text-red-400">{quizError}</p>
            )}
            <div className="flex justify-end mt-6">
              {!isLast ? (
                <button
                  onClick={() => setCurrentIndex((i) => i + 1)}
                  disabled={!answers[q.id]}
                  className="flex items-center gap-1.5 px-5 py-2.5 bg-violet-600 hover:bg-violet-700 disabled:opacity-40 text-white rounded-xl text-sm font-medium"
                >
                  {t('credentialQuiz.next')} <ChevronRight size={16} />
                </button>
              ) : (
                <button
                  onClick={submit}
                  disabled={!allAnswered || loading}
                  className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white rounded-xl text-sm font-medium"
                >
                  {loading ? t('credentialQuiz.submitting') : t('credentialQuiz.submitQuiz')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ---- Topic browse view ----
  return (
    <div className="min-h-full max-w-3xl mx-auto p-6" style={{ background: '#09090f' }}>
      <div className="flex items-center gap-3 mb-2">
        <GraduationCap className="w-7 h-7 text-violet-400" />
        <h1 className="text-2xl font-bold text-white">{t('credentialQuiz.pageTitle')}</h1>
      </div>
      <p className="text-gray-400 text-sm mb-8">
        {t('credentialQuiz.pageSubtitlePrefix', { count: topics.length })}{' '}
        <Link to="/settings/help-center" className="text-violet-400 hover:text-violet-300 underline">{t('credentialQuiz.helpCenterLink')}</Link>).
      </p>

      {loading && topics.length === 0 && <p className="text-gray-500 text-sm">{t('credentialQuiz.loadingTopics')}</p>}

      {Object.entries(grouped).map(([groupLabel, groupTopics]) => (
        <div key={groupLabel} className="mb-8">
          <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wide mb-3">{groupLabel}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {groupTopics.map((topic) => (
              <div key={topic.id} className="bg-white/5 border border-white/10 rounded-2xl p-4">
                <h3 className="text-white font-medium mb-1">{topic.name}</h3>
                {topic.description && <p className="text-xs text-gray-500 mb-3">{topic.description}</p>}
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs text-gray-500">{t('credentialQuiz.mastery')}</span>
                  <span className="text-xs text-white font-medium">{topic.topic_score.toFixed(1)} / 5.0</span>
                </div>
                <div className="flex gap-1.5">
                  {Object.keys(LEVEL_MAX).map((level) => {
                    const done = topic.levels[level];
                    return (
                      <button
                        key={level}
                        onClick={() => startQuiz(topic.id, level)}
                        className={`flex-1 flex items-center justify-center gap-1 px-2 py-2 rounded-lg text-xs font-medium border transition-all ${
                          done
                            ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-300'
                            : 'bg-gray-900/40 border-gray-700/50 text-gray-300 hover:border-violet-500/50'
                        }`}
                        title={done ? t('credentialQuiz.retakeTitle', { score: done.level_score.toFixed(1), max: LEVEL_MAX[level] }) : t('credentialQuiz.takeQuizTitle', { level: levelLabel(level, t) })}
                      >
                        {done && <RotateCcw size={11} />}
                        {levelLabel(level, t)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CredentialQuizPage;
